import React, { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Dialog, TextField } from '../ui';
import { ChevronLeftIcon, ChevronRightIcon, EditIcon, RefreshIcon } from '../ui/icons';
import { lookalikeTypes, uiLang, useKinds } from '../kinds';
import { SOURCE_LABELS } from '../sources';

/**
 * "New item type": start from one of the kinds' models (name it, create it
 * here), or from a blank type (its editor page, /settings/item-types/new).
 * Choosing a model the caller already has a plain type for (Movies, Séries…)
 * suggests converting that one instead.
 */
function CreateItemTypeDialog({ itemTypes = [], onClose, onCreate }) {
  const { t, i18n } = useTranslation();
  const lang = uiLang(i18n);
  const kinds = useKinds();
  const navigate = useNavigate();
  const [presetChoice, setPresetChoice] = useState(null);   // {id, labels, fields}
  const [presetName, setPresetName] = useState('');
  const [busy, setBusy] = useState(false);
  // Each step is its own dialog (a key apiece): switching steps places the focus again.
  const nameRef = useRef(null);
  const lookalikes = useMemo(() => (presetChoice ? lookalikeTypes(itemTypes, presetChoice.id) : []), [itemTypes, presetChoice]);
  const quoted = (name) => (lang === 'fr' ? `« ${name} »` : `“${name}”`);

  const submit = async (e) => {
    e.preventDefault();
    if (!presetName.trim()) return;
    setBusy(true);
    try {
      await onCreate({ from_preset: presetChoice.id, name: presetName.trim(), lang });
    } finally {
      setBusy(false);
    }
  };

  if (!presetChoice) {
    return (
      <Dialog key="choose" open onClose={onClose} title={t('itemTypes.create')} description={t('itemTypeForm.start.intro')} size="lg"
              footer={<Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>}>
        <ul className="preset-grid">
          {kinds.flatMap(kind => kind.presets).map(preset => (
            <li key={preset.id}>
              <button type="button" className="preset-choice"
                      onClick={() => { setPresetChoice(preset); setPresetName(preset.labels[lang]); }}>
                <span className="preset-choice__title">{preset.labels[lang]}</span>
                <span className="preset-choice__hint">
                  {t('itemTypeForm.start.fieldCount', { count: preset.fields.length })}
                  {preset.source === 'tmdb' && ` · ${t('itemTypeForm.start.fromTmdb')}`}
                  {preset.source !== 'tmdb' && (preset.sources || []).length > 0
                    && ` · ${t('itemTypeForm.start.fromSources', { sources: preset.sources.map(id => SOURCE_LABELS[id] || id).join(', ') })}`}
                </span>
                <ChevronRightIcon className="preset-choice__chevron" />
              </button>
            </li>
          ))}
          <li>
            <button type="button" className="preset-choice preset-choice--blank"
                    onClick={() => { onClose(); navigate('/settings/item-types/new'); }}>
              <span className="preset-choice__title"><EditIcon size={16} /> {t('itemTypeForm.start.blank')}</span>
              <span className="preset-choice__hint">{t('itemTypeForm.start.blankHelp')}</span>
              <ChevronRightIcon className="preset-choice__chevron" />
            </button>
          </li>
        </ul>
      </Dialog>
    );
  }

  return (
    <Dialog
      key="model"
      open
      onClose={onClose}
      initialFocusRef={nameRef}
      title={presetChoice.labels[lang]}
      description={t('itemTypeForm.start.presetIntro', { model: presetChoice.labels[lang] })}
      size="md"
      footer={(
        <>
          <Button variant="ghost" icon={ChevronLeftIcon} onClick={() => setPresetChoice(null)} className="dialog-footer__back">
            {t('itemTypeForm.start.back')}
          </Button>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" form="create-item-type" disabled={!presetName.trim()} loading={busy}>{t('itemTypeForm.create')}</Button>
        </>
      )}
    >
      <form id="create-item-type" className="dialog-form" onSubmit={submit}>
        {lookalikes.length > 0 && (
          <Alert tone="warning" role="note">
            <p>{t('itemTypeForm.start.lookalike', { count: lookalikes.length, names: lookalikes.map(type => quoted(type.name)).join(', ') })}</p>
            <div className="settings-actions">
              {lookalikes.map(type => (
                <Button key={type.id} as={Link} to={`/settings/item-types/${type.id}/convert`} size="sm" variant="secondary"
                        icon={RefreshIcon} onClick={onClose}>
                  {t('itemTypeForm.start.convertInstead', { name: type.name })}
                </Button>
              ))}
            </div>
          </Alert>
        )}
        <div className="dialog-preview">
          <p className="dialog-preview__title">{t('itemTypeForm.start.fieldsOfModel')}</p>
          <ul className="tag-list">
            {presetChoice.fields.map(field => <li key={field.key} className="ui-chip ui-chip--static">{field.labels[lang]}</li>)}
          </ul>
        </div>
        <TextField ref={nameRef} label={t('itemTypeForm.name')} value={presetName} onChange={(e) => setPresetName(e.target.value)} required />
      </form>
    </Dialog>
  );
}

export default CreateItemTypeDialog;
